import { z } from "zod";

type FlagValue = boolean | string | number | Record<string, unknown> | unknown[];

interface FeatureFlagDefinition<TFlagValue extends FlagValue = FlagValue> {
  defaultValue: TFlagValue;
  nonProdDefaultValue: TFlagValue;
  streaming: boolean;
  validator: z.ZodType<TFlagValue>;
}

export type FeatureFlagDefinitions = Record<string, FeatureFlagDefinition>;

// ************************************************************************************************
// Feature Flag Definitions
//
// Every key in the definitions object below should map to a feature flag key in LaunchDarkly.
// See https://www.notion.so/ediphi/LaunchDarkly-Feature-Flags-1896767fc83f80c097b1d7cae65625aa.
//
// When using an enum flag in LaunchDarkly, define and use the same enum values here.
// ************************************************************************************************

export const FEATURE_FLAG_DEFINITIONS = {
  "last-mile-enable-budget-import": {
    defaultValue: false,
    nonProdDefaultValue: false,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-enable-join-export": {
    defaultValue: false,
    nonProdDefaultValue: false,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-enable-procore-export": {
    defaultValue: false,
    nonProdDefaultValue: false,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-enable-arcol-import": {
    defaultValue: false,
    nonProdDefaultValue: true,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-togal-integration": {
    defaultValue: false,
    nonProdDefaultValue: true,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-autodesk-qto": {
    defaultValue: false,
    nonProdDefaultValue: true,
    streaming: true,
    validator: z.boolean(),
  },
  "last-mile-ost-qto": {
    defaultValue: false,
    nonProdDefaultValue: true,
    streaming: true,
    validator: z.boolean(),
  },
} as const satisfies FeatureFlagDefinitions;

export type FeatureFlags = {
  [K in keyof typeof FEATURE_FLAG_DEFINITIONS]: z.infer<(typeof FEATURE_FLAG_DEFINITIONS)[K]["validator"]>;
};
